"use client";

import { useCart } from "@/src/contexts/cart-context";
import {
  clearCart,
  deleteLineItem,
  updateLineItem,
} from "@/modules/cart/actions/cart";
import { Button } from "@/modules/common/components/button";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import Image from "next/image";
import { useParams, useRouter } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { useLocationStore, useToastStore } from "@/store/index";

interface CartDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CartDropdown({ isOpen, onClose }: CartDropdownProps) {
  const { cart, refreshCart } = useCart();
  const params = useParams();
  const router = useRouter();
  const countryCode = (params.countryCode as string) || "cu";
  const { showToast } = useToastStore();
  const { province, municipality } = useLocationStore();

  const [loading, setLoading] = useState<string | null>(null);
  const [localQuantities, setLocalQuantities] = useState<
    Record<string, number>
  >({});
  const handleUpdateRef = useRef<NodeJS.Timeout>(undefined);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sincronizar cantidades locales con el carrito real
  useEffect(() => {
    if (cart?.items) {
      const counts = cart.items.reduce(
        (acc, item) => ({ ...acc, [item.id]: item.quantity }),
        {}
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocalQuantities(counts);
    }
  }, [cart?.items]);

  // Cerrar al click exterior
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      )
        onClose();
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const handleUpdate = (lineId: string, newQuantity: number) => {
    if (newQuantity < 1) return;

    // 1. Optimistic Update: Cambia visualmente al instante
    setLocalQuantities((prev) => ({ ...prev, [lineId]: newQuantity }));

    // 2. Debounce: Espera a que el usuario termine de clickear antes de enviar al server
    clearTimeout(handleUpdateRef.current);
    handleUpdateRef.current = setTimeout(async () => {
      setLoading(lineId);
      const res = await updateLineItem({
        lineId,
        quantity: newQuantity,
        countryCode,
        province,
        municipality,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((res as any)?.error) showToast((res as any).error, "err");
      await refreshCart();
      setLoading(null);
    }, 450);
  };

  const handleRemove = async (lineId: string) => {
    setLoading(lineId);
    await deleteLineItem(lineId);
    await refreshCart();
    setLoading(null);
  };

  const handleEmptyCart = async () => {
    setLoading("all");
    await clearCart();
    await refreshCart();
    setLoading(null);
  };

  if (!isOpen) return null;

  const items = cart?.items || [];

  return (
    <div
      ref={dropdownRef}
      className="border-green-main bg-cream animate-in fade-in zoom-in-95 fixed inset-x-4 top-24 z-50 w-[calc(100vw-2rem)] overflow-hidden rounded-xl border shadow-2xl duration-200 sm:absolute sm:inset-auto sm:top-16 sm:right-0 sm:w-100"
    >
      <div className="p-4">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-achiko text-green-main text-xl font-bold">
            Productos ({items.length})
          </h3>
          {items.length > 0 && (
            <button
              onClick={handleEmptyCart}
              disabled={!!loading}
              className="flex items-center gap-1 text-xs text-gray-500 transition-colors hover:text-red-500"
            >
              <Trash2 className="h-3 w-3" /> Vaciar carrito
            </button>
          )}
        </div>

        <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto pr-1">
          {items.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <ShoppingBag className="mx-auto mb-2 h-12 w-12 opacity-20" />
              <p>Tu carrito está vacío</p>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className="border-green-main relative flex gap-3 rounded-lg border bg-white p-3"
              >
                <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md border border-gray-100">
                  <Image
                    src={item.thumbnail || "/placeholder-product.png"}
                    alt={item.title}
                    fill
                    className="object-cover"
                  />
                </div>

                <div className="flex flex-1 flex-col justify-between">
                  <div>
                    <h4 className="font-inter line-clamp-1 text-sm font-semibold text-gray-800">
                      {item.title}
                    </h4>
                    <p className="line-clamp-1 text-xs text-gray-500">
                      {item.variant?.title}
                    </p>
                  </div>

                  <div className="mt-2 flex items-center gap-2">
                    <div className="bg-green-main flex h-7 items-center rounded-md text-white">
                      <button
                        onClick={() =>
                          handleUpdate(
                            item.id,
                            (localQuantities[item.id] || item.quantity) - 1
                          )
                        }
                        disabled={
                          localQuantities[item.id] <= 1 || loading === item.id
                        }
                        className="h-full px-2 hover:bg-black/10 disabled:opacity-30"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="min-w-5 px-2 text-center text-xs font-bold">
                        {localQuantities[item.id] ?? item.quantity}
                      </span>
                      <button
                        onClick={() =>
                          handleUpdate(
                            item.id,
                            (localQuantities[item.id] || item.quantity) + 1
                          )
                        }
                        disabled={loading === item.id}
                        className="h-full px-2 hover:bg-black/10"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>

                    <span className="text-orange-main ml-auto text-sm font-bold">
                      $
                      {(
                        (item.variant?.calculated_price?.calculated_amount ||
                          0) * (localQuantities[item.id] || item.quantity)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => handleRemove(item.id)}
                  className="absolute top-2 right-2 text-gray-400 transition-colors hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="border-green-main/20 mt-4 space-y-4 border-t pt-4">
          <div className="flex items-center justify-between font-bold text-gray-800">
            <span>Total de compra</span>
            <span>${cart?.total?.toFixed(2) || "0.00"}</span>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              onClick={() => {
                onClose();
                router.push(`/${countryCode}`);
              }}
              className="border-green-main bg-green-main/10 text-green-main hover:bg-orange-main w-full gap-2 border-2 py-6 text-base font-semibold hover:text-white"
            >
              Continuar comprando
            </Button>
            <Button
              onClick={() => {
                onClose();
                const currentQuery = new URLSearchParams(window.location.search).toString();
                const queryString = currentQuery ? `?${currentQuery}` : "";
                router.push(`/${countryCode}/checkout${queryString}`);
              }}
              disabled={items.length <= 0}
              className="bg-green-main w-full py-6 text-base font-semibold text-white hover:bg-[#047a2e]"
            >
              Proceder con el pago
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
